/* eslint-disable react/prop-types */
/**
 * BillPreviewDialog — Interaction Refinement, item 3.
 *
 * Shows a preview of the printed bill BEFORE BILL_PRINTED fires and
 * the invoice number is allocated. This is a workspace-class dialog
 * (a printed bill needs vertical room) but strictly bounded — never
 * full-screen. The billing screen stays visible at the edges.
 *
 * Actions:
 *   Print bill · primary, focused, Enter triggers → BILL_PRINTED
 *   Send bill   · opens the layout-only send options panel
 *   Cancel      · closes, order stays OPEN, no invoice consumed
 */
import React, { useEffect, useRef, useState } from "react";
import { Printer, Send, X as XIcon, Smartphone, CornerDownLeft } from "lucide-react";
import { ITEMS } from "./mockData";
import { computeTotals, computeTotalsFor, getActive, staffNameFor } from "./posReducer";
import { TABLES } from "./mockData";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../components/ui/dialog";

const fmt2 = (n) => Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// Part 10 · consolidate identical lines for the printed bill only.
// A line "matches" another when item_id + unit_price + discount +
// note are all identical. Merged rows sum qty; ids of merged lines
// are joined so React key stays stable within a print session.
function consolidateLines(lines) {
  const buckets = new Map();
  const order = [];
  for (const l of lines) {
    const key = [
      l.item_id, l.unit_price, Number(l.discount || 0), (l.note || "").trim(),
      l.is_open_item ? "open" : "menu",
    ].join("¦");
    if (!buckets.has(key)) {
      order.push(key);
      buckets.set(key, { ...l, id: `merged_${l.id}`, qty: 0, _srcIds: [] });
    }
    const b = buckets.get(key);
    b.qty += Number(l.qty || 0);
    b._srcIds.push(l.id);
  }
  return order.map((k) => buckets.get(k));
}

// Part 11 · look up which invoice numbers replaced a given (voided) one.
// Reads from session.invoice_voids + session.invoice_replacements.
function replacementsFor(state, invoiceNo) {
  if (!invoiceNo || !state?.session) return [];
  const rep = state.session.invoice_replacements || {};
  const kids = Array.isArray(rep[invoiceNo]) ? rep[invoiceNo] : [];
  return kids;
}

export default function BillPreviewDialog({ open, state, outletName, mode = "print", voidedOrderId, voidRecord, onCancel, onConfirmPrint }) {
  const activeFallback = getActive(state) || { order: {}, lines: [], payments: [] };
  // Part 7.2 · when voidedOrderId is passed, render THAT slice as a
  // read-only voided bill instead of the live active order. Totals
  // still compute over the referenced slice via computeTotalsFor.
  const active = voidedOrderId && state?.orders?.[voidedOrderId]
    ? state.orders[voidedOrderId]
    : activeFallback;
  const isVoidedView = !!voidedOrderId;
  const totals = isVoidedView
    ? computeTotalsFor(state, voidedOrderId)
    : computeTotals(state);
  const printBtnRef = useRef(null);
  const [sendPanel, setSendPanel] = useState(false);
  const [phone, setPhone] = useState("");
  const [channel, setChannel] = useState("sms");
  const [payCash, setPayCash]   = useState(true);
  const [payOnline, setPayOnline] = useState(true);
  const isReprint = mode === "reprint";

  useEffect(() => {
    if (open) {
      setSendPanel(false);
      setPhone(active.order?.guest_phone || "");
      setTimeout(() => printBtnRef.current?.focus(), 30);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const onKey = (e) => {
    if (e.key === "Escape") { e.preventDefault(); onCancel(); return; }
    if (e.key === "Enter" && !sendPanel) { e.preventDefault(); onConfirmPrint(); }
  };

  const table = TABLES.find((t) => t.id === active.order.table_id);

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onCancel(); }}>
      <DialogContent
        onKeyDown={onKey}
        data-testid="pos-bill-preview-dialog"
        // Workspace-class: taller than a prompt but never full-screen.
        // At 1366×768 this leaves ~130px of the billing screen visible
        // above and below the modal.
        className="max-w-[560px] w-[92vw] max-h-[92vh] p-0 gap-0 rounded-lg border border-[color:var(--pos-color-border,#E2E8DF)] bg-white overflow-hidden flex flex-col">
        <DialogHeader className="px-4 py-2 border-b border-[color:var(--pos-color-border,#E2E8DF)] flex-row items-center justify-between space-y-0">
          <div>
            <DialogTitle className="text-[12px] font-semibold text-[color:var(--pos-color-primary-text,#1A2421)]">
              {isVoidedView ? "Voided invoice" : isReprint ? "Bill reprint" : "Bill preview"}
            </DialogTitle>
            <DialogDescription className="text-[10px] text-[color:var(--pos-color-secondary-text,#5C6B64)]">
              {isVoidedView
                ? `Read-only · this invoice has been voided by the manager.`
                : isReprint
                  ? `Same invoice number will be reprinted${active.order.invoice_no ? ` · ${active.order.invoice_no}` : ""}${active.order.reprint_count ? ` · reprint count ${active.order.reprint_count + 1}` : " · first reprint"}.`
                  : "Not printed yet — no invoice number consumed. Confirm below."}
            </DialogDescription>
          </div>
          <button type="button" onClick={onCancel}
            className="inline-flex items-center gap-1 h-7 px-2.5 rounded border border-[color:var(--pos-color-border,#E2E8DF)] text-[10px] text-[color:var(--pos-color-secondary-text,#5C6B64)] hover:bg-[color:var(--pos-color-raised-surface,#F9F9F8)]"
            data-testid="pos-bill-preview-cancel">
            <XIcon className="w-3 h-3" /> Esc
          </button>
        </DialogHeader>

        {/* Part 7.2 · VOIDED banner across the receipt paper */}
        {isVoidedView && (
          <div className="px-4 py-2 bg-rose-50 border-b border-rose-200 flex items-center flex-wrap gap-x-2 gap-y-0.5"
            data-testid="pos-bill-preview-voided-banner">
            <span className="text-[14px] font-bold uppercase tracking-widest text-rose-800">VOIDED</span>
            {voidRecord?.voided_invoice && (
              <span className="text-[11px] font-semibold text-rose-900 tabular-nums">
                · {voidRecord.voided_invoice}
              </span>
            )}
            {(voidRecord?.reason_code || active.order.void_reason) && (
              <span className="text-[11px] text-rose-900">
                · {voidRecord?.reason_code || active.order.void_reason}
              </span>
            )}
            {(voidRecord?.authorised_by || active.order.void_authorised_by) && (
              <span className="text-[11px] text-rose-900 tabular-nums">
                · authorised by {voidRecord?.authorised_by || active.order.void_authorised_by}
              </span>
            )}
            {voidRecord?.note && (
              <span className="text-[11px] text-rose-900 italic w-full">
                &ldquo;{voidRecord.note}&rdquo;
              </span>
            )}
          </div>
        )}

        {/* POS Control Sprint · REPRINT watermark banner. Rendered on
            the reprint preview so the printed slip always advertises
            itself as a duplicate copy — never mistaken for the
            original fiscal invoice. The reprint counter and
            date/time-stamp read directly from the order state so
            the strip matches the audit line inside the receipt body. */}
        {isReprint && (
          <div className="px-4 py-2 bg-purple-50 border-b border-purple-200 flex items-center flex-wrap gap-x-2 gap-y-0.5"
            data-testid="pos-bill-preview-reprint-banner">
            <span className="text-[14px] font-bold uppercase tracking-widest text-purple-800">REPRINT</span>
            {active.order.invoice_no && (
              <span className="text-[11px] font-semibold text-purple-900 tabular-nums">
                · {active.order.invoice_no}
              </span>
            )}
            {active.order.reprint_count > 0 && (
              <span className="text-[11px] text-purple-900 tabular-nums">
                · copy #{active.order.reprint_count}
              </span>
            )}
            <span className="text-[11px] text-purple-900 tabular-nums">
              · {new Date(active.order.last_reprint_at || Date.now()).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}
            </span>
          </div>
        )}

        {/* Preview body — mimics receipt paper */}
        <div className="flex-1 overflow-y-auto bg-[color:var(--pos-color-raised-surface,#F9F9F8)] p-4">
          <div className="mx-auto w-[92%] bg-white rounded shadow-sm border border-[color:var(--pos-color-border,#E2E8DF)] p-4 text-[color:var(--pos-color-primary-text,#1A2421)]"
            data-testid="pos-bill-preview-body">
            {/* Outlet block */}
            <div className="text-center pb-2 border-b border-dashed border-[#C6D0CC]">
              <div className="text-[15px] font-bold uppercase tracking-wider">Woodland Eatery</div>
              <div className="text-[10px] text-[color:var(--pos-color-secondary-text,#5C6B64)]">42 MG Road · Bengaluru 560001 · GSTIN 29ABCDE1234F1Z5</div>
              <div className="text-[10px] text-[color:var(--pos-color-secondary-text,#5C6B64)]">{outletName}</div>
            </div>

            {/* Meta */}
            <div className="grid grid-cols-2 gap-x-3 gap-y-0.5 py-2 border-b border-dashed border-[#C6D0CC] text-[10px]">
              <div><span className="text-[color:var(--pos-color-secondary-text,#5C6B64)]">Invoice</span> <strong className="tabular-nums" data-testid="pos-bill-preview-invoice-no">{active.order.invoice_no || "— to be allocated"}</strong></div>
              <div className="text-right"><span className="text-[color:var(--pos-color-secondary-text,#5C6B64)]">Date</span> <strong className="tabular-nums">{new Date().toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}</strong></div>
              <div><span className="text-[color:var(--pos-color-secondary-text,#5C6B64)]">Type</span> <strong>{active.order.type?.replace("_", " ") || "—"}</strong></div>
              <div className="text-right"><span className="text-[color:var(--pos-color-secondary-text,#5C6B64)]">Steward</span> <strong>{active.order.steward_name || staffNameFor(state, active.order.steward_id) || "—"}</strong></div>
              {active.order.type === "DINE_IN" && (<>
                <div><span className="text-[color:var(--pos-color-secondary-text,#5C6B64)]">Table</span> <strong>{table?.label || "—"}</strong></div>
                <div className="text-right"><span className="text-[color:var(--pos-color-secondary-text,#5C6B64)]">Covers</span> <strong className="tabular-nums">{active.order.covers ?? "—"}</strong></div>
              </>)}
              {active.order.token && (
                <div className="col-span-2"><span className="text-[color:var(--pos-color-secondary-text,#5C6B64)]">Token</span> <strong className="tabular-nums">{active.order.token}</strong></div>
              )}
              {/* Part 5.2 · delivery orders carry guest details on the bill */}
              {(active.order.guest_name || active.order.guest_phone || active.order.guest_address) && (
                <div className="col-span-2 border-t border-dashed border-[#C6D0CC] mt-1 pt-1 space-y-0.5"
                  data-testid="pos-bill-preview-guest">
                  {active.order.guest_name && (
                    <div><span className="text-[color:var(--pos-color-secondary-text,#5C6B64)]">Guest</span> <strong>{active.order.guest_name}</strong></div>
                  )}
                  {active.order.guest_phone && (
                    <div><span className="text-[color:var(--pos-color-secondary-text,#5C6B64)]">Phone</span> <strong className="tabular-nums">{active.order.guest_phone}</strong></div>
                  )}
                  {active.order.guest_address && (
                    <div data-testid="pos-bill-preview-address"><span className="text-[color:var(--pos-color-secondary-text,#5C6B64)]">Deliver to</span> <strong>{active.order.guest_address}</strong></div>
                  )}
                </div>
              )}
              {/* Item 3 (Sprint 4 corrections) · the voided-invoice
                  linkage is AUDIT information, not GUEST information.
                  Hidden on the print-to-guest bill. Still shown on the
                  reprint view (staff surface), on the voided-invoice
                  view, and in the exception report. */}
              {isReprint && active.order.parent_voided_invoice && (
                <div className="col-span-2 text-amber-800" data-testid="pos-bill-preview-parent-voided">
                  <span className="text-[color:var(--pos-color-secondary-text,#5C6B64)]">Replaces voided invoice</span> <strong className="tabular-nums">{active.order.parent_voided_invoice}</strong>
                </div>
              )}
              {isReprint && active.order.voided_invoice && (
                <div className="col-span-2 text-amber-800" data-testid="pos-bill-preview-voided-linkage">
                  <span className="text-[color:var(--pos-color-secondary-text,#5C6B64)]">Prior invoice</span> <strong className="tabular-nums">{active.order.voided_invoice}</strong> <span className="text-[color:var(--pos-color-secondary-text,#5C6B64)]">voided by manager</span>
                </div>
              )}
              {/* POS Control Sprint · smaller reprint audit line. */}
              {isReprint && (
                <div className="col-span-2 text-[color:var(--pos-color-secondary-text,#5C6B64)]"
                  data-testid="pos-bill-preview-reprint-audit-line">
                  <span>Reprint copy · </span>
                  <strong className="tabular-nums">
                    {new Date(active.order.last_reprint_at || Date.now()).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}
                  </strong>
                  {active.order.reprint_count > 0 && (
                    <span className="tabular-nums"> · copy #{active.order.reprint_count}</span>
                  )}
                </div>
              )}
              {/* Show any child invoices that replaced THIS one (if this preview happens to be for the voided parent — rare). */}
              {isReprint && active.order.invoice_no && replacementsFor(state, active.order.invoice_no).length > 0 && (
                <div className="col-span-2 text-amber-800" data-testid="pos-bill-preview-replaced-by">
                  <span className="text-[color:var(--pos-color-secondary-text,#5C6B64)]">Voided · replaced by</span>{" "}
                  <strong className="tabular-nums">{replacementsFor(state, active.order.invoice_no).join(", ")}</strong>
                </div>
              )}
            </div>

            {/* Line items — Part 10: consolidate identical lines
                (same item, unit_price, note) into a single row here
                on the printed bill only. The order ticket still shows
                them separately because unfired/fired distinction
                matters during service. */}
            <div className="py-2 border-b border-dashed border-[#C6D0CC]">
              {/* Part 5.2 (Sprint 3) · fixed column widths so every row —
                  qty=1 or qty=13 — aligns on the SAME vertical axis. The
                  outer grid is scoped by CSS variables so header +
                  every line share identical column widths. */}
              <div className="grid grid-cols-[1fr_3.5rem_5rem_6rem] gap-x-2 text-[10px] font-semibold uppercase text-[color:var(--pos-color-secondary-text,#5C6B64)] pb-1">
                <div>Item</div>
                <div className="text-right tabular-nums">Qty</div>
                <div className="text-right tabular-nums">Rate</div>
                <div className="text-right tabular-nums">Amount</div>
              </div>
              {consolidateLines(active.lines).map((l) => {
                const amount = l.qty * l.unit_price;
                return (
                  <div key={l.id} className="py-1 text-[11px]" data-testid={`pos-bill-preview-line-${l.id}`}>
                    <div className="grid grid-cols-[1fr_3.5rem_5rem_6rem] gap-x-2">
                      <div className="truncate">{l.item_name}</div>
                      <div className="text-right tabular-nums">{l.qty}</div>
                      <div className="text-right tabular-nums">{fmt2(l.unit_price)}</div>
                      <div className="text-right tabular-nums">{fmt2(amount)}</div>
                    </div>
                    {/* Sprint 2 Correction Part 3 · kitchen notes are
                        instructions for the kitchen. They do NOT
                        belong on a guest bill. The note is still on
                        the fire slip (Sprint3.js TicketSlipDialog) and
                        in the order panel (BillingScreen LineRow). */}
                  </div>
                );
              })}
            </div>

            {/* Totals */}
            <div className="py-2 text-[11px]">
              <TotalRow label="Subtotal" value={totals.subtotal} />
              {totals.discount > 0 && <TotalRow label="Discount" value={-totals.discount} />}
              {totals.has_food && totals.cgst > 0 && <TotalRow label="CGST @ 2.5%" value={totals.cgst} testid="pos-bill-preview-cgst" />}
              {totals.has_food && totals.sgst > 0 && <TotalRow label="SGST @ 2.5%" value={totals.sgst} testid="pos-bill-preview-sgst" />}
              {totals.has_liquor && totals.vat > 0 && <TotalRow label="VAT @ 20%"  value={totals.vat}  testid="pos-bill-preview-vat" />}
              {totals.service_charge_enabled && totals.service_charge > 0 && (
                <TotalRow label="Service charge" value={totals.service_charge} testid="pos-bill-preview-service-charge" />
              )}
              {Math.abs(totals.round_off) > 0.005 && <TotalRow label="Round off" value={totals.round_off} testid="pos-bill-preview-round-off" />}
              <div className="flex items-center justify-between pt-1.5 mt-1.5 border-t border-[#1A2421]">
                <div className="text-[13px] font-semibold uppercase tracking-wider">Grand total</div>
                <div className="text-[16px] font-bold tabular-nums" data-testid="pos-bill-preview-grand-total">₹{fmt2(totals.grand_total)}</div>
              </div>
            </div>

            {/* Part 9 · Tenders (with reference for card / upi / room).
                Financial Ground Truth (Feb 2026) · a cash tender that
                carries change shows both `amount_received` and the
                explicit `change_due` so the printed receipt matches
                what the cashier told the guest at settlement. */}
            {active.payments && active.payments.length > 0 && (
              <div className="py-2 border-t border-dashed border-[#C6D0CC] text-[10px]"
                data-testid="pos-bill-preview-tenders">
                <div className="uppercase font-semibold text-[color:var(--pos-color-secondary-text,#5C6B64)] mb-1">Tenders</div>
                {active.payments.map((p) => {
                  const isCashOver = p.mode === "cash"
                    && Number(p.change_due) > 0.005;
                  return (
                    <div key={p.id}
                      data-testid={`pos-bill-preview-tender-${p.id}`}>
                      <div className="flex items-center justify-between py-0.5">
                        <div>
                          <span className="text-[color:var(--pos-color-primary-text,#1A2421)] font-medium capitalize">
                            {String(p.mode).replace("_", " ")}
                          </span>
                          {p.reference && (
                            <span className="ml-1 text-[color:var(--pos-color-secondary-text,#5C6B64)]"
                              data-testid={`pos-bill-preview-tender-${p.id}-ref`}>
                              · ref <strong className="text-[color:var(--pos-color-primary-text,#1A2421)] tabular-nums">{p.reference}</strong>
                            </span>
                          )}
                        </div>
                        <div className="tabular-nums">₹{fmt2(p.amount)}</div>
                      </div>
                      {isCashOver && (
                        <div className="pl-2 text-[9px] text-[color:var(--pos-color-secondary-text,#5C6B64)] tabular-nums"
                          data-testid={`pos-bill-preview-tender-${p.id}-change`}>
                          <div className="flex justify-between">
                            <span>Cash received</span>
                            <strong className="text-[color:var(--pos-color-primary-text,#1A2421)]">
                              ₹{fmt2(p.amount_received)}
                            </strong>
                          </div>
                          <div className="flex justify-between">
                            <span>Change due</span>
                            <strong className="text-[color:var(--pos-color-primary-text,#1A2421)]">
                              ₹{fmt2(p.change_due)}
                            </strong>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            <div className="text-center text-[10px] text-[color:var(--pos-color-secondary-text,#5C6B64)] pt-1">Thank you · Please visit again</div>
          </div>
        </div>

        {sendPanel
          ? <SendPanel phone={phone} setPhone={setPhone}
              channel={channel} setChannel={setChannel}
              payCash={payCash} setPayCash={setPayCash}
              payOnline={payOnline} setPayOnline={setPayOnline}
              onBack={() => setSendPanel(false)} />
          : isVoidedView
            ? (
              <div className="border-t border-[color:var(--pos-color-border,#E2E8DF)] px-3 py-2 flex items-center justify-end gap-2 bg-white">
                <button type="button" onClick={onCancel}
                  data-testid="pos-bill-preview-voided-close"
                  className="h-10 px-4 rounded bg-[#1A2421] text-white text-[12px] font-semibold hover:opacity-90">
                  Close
                </button>
              </div>
            )
            : (
            <div className="border-t border-[color:var(--pos-color-border,#E2E8DF)] px-3 py-2 flex items-center justify-between gap-2 bg-white">
              <button type="button" onClick={onCancel}
                data-testid="pos-bill-preview-cancel-btn"
                className="h-10 px-3 rounded text-[12px] font-medium text-[color:var(--pos-color-secondary-text,#5C6B64)] hover:bg-[color:var(--pos-color-page-bg,#F4F6F2)]">
                Cancel
              </button>
              <div className="flex items-center gap-2">
                <button type="button" onClick={() => setSendPanel(true)}
                  data-testid="pos-bill-preview-send"
                  className="h-10 px-4 rounded border border-[color:var(--color-primary)] bg-white text-[color:var(--color-primary)] text-[12px] font-semibold inline-flex items-center gap-1.5 hover:bg-[color:var(--color-primary)]/[0.06]">
                  <Send className="w-3.5 h-3.5" /> Send bill
                </button>
                <button type="button" ref={printBtnRef} onClick={onConfirmPrint}
                  data-testid="pos-bill-preview-print"
                  className="h-10 px-5 rounded bg-[color:var(--color-primary)] hover:bg-[color:var(--color-primary-hover)] text-white text-[13px] font-semibold inline-flex items-center gap-1.5 focus:outline-none focus:ring-2 ring-offset-2 ring-[color:var(--color-primary)]">
                  <Printer className="w-3.5 h-3.5" /> {isReprint ? "Reprint" : "Print bill"}
                  <CornerDownLeft className="w-3 h-3 opacity-80" />
                </button>
              </div>
            </div>
          )}
      </DialogContent>
    </Dialog>
  );
}

function TotalRow({ label, value, testid }) {
  return (
    <div className="flex items-center justify-between text-[11px] py-0.5" data-testid={testid}>
      <span className="text-[color:var(--pos-color-secondary-text,#5C6B64)]">{label}</span>
      <span className="tabular-nums">₹{fmt2(value)}</span>
    </div>
  );
}

// ─── Send options — UI only ─────────────────────────────────────────
function SendPanel({ phone, setPhone, channel, setChannel, payCash, setPayCash, payOnline, setPayOnline, onBack }) {
  return (
    <div className="border-t border-[color:var(--pos-color-border,#E2E8DF)] px-4 py-3 bg-[color:var(--pos-color-raised-surface,#F9F9F8)]" data-testid="pos-send-panel">
      <div className="flex items-center justify-between mb-2">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-[color:var(--pos-color-secondary-text,#5C6B64)] font-medium">Send bill to guest</div>
          <div className="text-[11px] text-[color:var(--pos-color-primary-text,#1A2421)]">Send bill to the guest via SMS or WhatsApp.</div>
        </div>
        <button type="button" onClick={onBack} className="text-[10px] text-[color:var(--pos-color-secondary-text,#5C6B64)] hover:text-[color:var(--pos-color-primary-text,#1A2421)] underline underline-offset-2"
          data-testid="pos-send-back">
          Back to preview
        </button>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <div>
          <div className="text-[9px] uppercase tracking-widest text-[color:var(--pos-color-secondary-text,#5C6B64)] mb-1">Guest phone</div>
          <div className="flex items-center gap-1 rounded border border-[color:var(--pos-color-border,#E2E8DF)] bg-white px-2 h-9">
            <Smartphone className="w-3.5 h-3.5 text-[color:var(--pos-color-secondary-text,#5C6B64)]" />
            <input value={phone} onChange={(e) => setPhone(e.target.value.replace(/[^0-9+ ]/g, ""))}
              inputMode="numeric" placeholder="+91 ..."
              data-testid="pos-send-phone"
              className="flex-1 h-full text-[12px] tabular-nums focus:outline-none" />
          </div>
        </div>
        <div>
          <div className="text-[9px] uppercase tracking-widest text-[color:var(--pos-color-secondary-text,#5C6B64)] mb-1">Channel</div>
          <div className="flex items-center gap-1">
            {[["sms", "SMS"], ["whatsapp", "WhatsApp"]].map(([id, label]) => (
              <button key={id} type="button" onClick={() => setChannel(id)}
                data-testid={`pos-send-channel-${id}`}
                className={`h-9 px-3 rounded border text-[11px] font-medium
                  ${channel === id
                    ? "border-[color:var(--color-primary)] bg-[color:var(--color-primary)] text-white"
                    : "border-[color:var(--pos-color-border,#E2E8DF)] bg-white text-[color:var(--pos-color-primary-text,#1A2421)] hover:bg-[#F0F0EE]"}`}>
                {label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="mt-2">
        <div className="text-[9px] uppercase tracking-widest text-[color:var(--pos-color-secondary-text,#5C6B64)] mb-1">Payment options in message</div>
        <label className="inline-flex items-center gap-1.5 text-[11px] text-[color:var(--pos-color-primary-text,#1A2421)] mr-3" data-testid="pos-send-pay-cash-label">
          <input type="checkbox" checked={payCash} onChange={(e) => setPayCash(e.target.checked)} data-testid="pos-send-pay-cash" />
          Pay cash on counter
        </label>
        <label className="inline-flex items-center gap-1.5 text-[11px] text-[color:var(--pos-color-primary-text,#1A2421)]" data-testid="pos-send-pay-online-label">
          <input type="checkbox" checked={payOnline} onChange={(e) => setPayOnline(e.target.checked)} data-testid="pos-send-pay-online" />
          Pay online
        </label>
      </div>

      <div className="mt-3 flex items-center justify-end gap-2">
        <button type="button" disabled
          data-testid="pos-send-btn"
          className="h-10 px-5 rounded bg-[#E2E8DF] text-[#B8B8B5] text-[12px] font-semibold cursor-not-allowed inline-flex items-center gap-1.5"
          title="Send is disabled in preview">
          <Send className="w-3.5 h-3.5" /> Send bill
        </button>
      </div>
    </div>
  );
}
